// ── KalshiClient — Authenticated REST client with key pool ──────
//
// Round-robins requests across multiple API keys for rate limit
// headroom. On 401/403, retries with the next key before throwing.
//
// Response unwrapping handles Kalshi's varying envelope shapes
// (market_positions, positions, results, data, etc.)

import { buildHeaders, getBaseUrl, type KalshiEnvironment } from '../lib/kalshi/auth';
import type { KalshiPosition, KalshiFill, KalshiOrder, OrderInput } from '../types/kalshi';
import { type KeyChannel, type RequestPurpose } from './KeyChannel';

// ── Types ───────────────────────────────────────────────────────

export interface ParsedKey {
  id?: string;        // internal UUID (from KalshiKey.id) — used for UI health matching
  accessKey: string;
  cryptoKey: CryptoKey;
  label: string;
}

// ── Helpers ─────────────────────────────────────────────────────

/** Convert Kalshi dollars string (e.g. "0.62") to cents (62). */
function dollarsToCents(dollars: unknown, fallbackCents?: unknown): number {
  if (typeof dollars === 'string' && dollars !== '') {
    const n = parseFloat(dollars);
    if (!isNaN(n)) return Math.round(n * 100);
  }
  if (typeof dollars === 'number') {
    // If <= 1.01, treat as probability → cents; otherwise already cents
    return dollars <= 1.01 ? Math.round(dollars * 100) : Math.round(dollars);
  }
  if (typeof fallbackCents === 'number') return Math.round(fallbackCents);
  return 0;
}

function pickString(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c !== '') return c;
  }
  return undefined;
}

function pickNumber(...candidates: unknown[]): number | undefined {
  for (const c of candidates) {
    if (typeof c === 'number' && !isNaN(c)) return c;
    if (typeof c === 'string' && c !== '') {
      const n = parseFloat(c);
      if (!isNaN(n)) return n;
    }
  }
  return undefined;
}

// ── Client ──────────────────────────────────────────────────────

export class KalshiClient {
  private channels: KeyChannel[];
  private channelIndex = 0;
  private baseUrl: string;

  constructor(channels: KeyChannel[], environment: KalshiEnvironment) {
    if (channels.length === 0) throw new Error('KalshiClient requires at least one channel');
    this.channels = channels;
    this.baseUrl = getBaseUrl(environment);
  }

  getChannels(): KeyChannel[] {
    return this.channels;
  }

  /**
   * Select the best channel for a given purpose.
   * - Filters out quarantined channels
   * - Among healthy channels, picks one with available budget
   * - Trading has a safety valve: if no budget, pick least-loaded anyway
   * - Polling/general: round-robin among eligible
   */
  private selectChannel(purpose: RequestPurpose): KeyChannel {
    const healthy = this.channels.filter((ch) => !ch.isQuarantined());
    if (healthy.length === 0) {
      // All quarantined — pick the one that expires soonest
      let best = this.channels[0];
      for (const ch of this.channels) {
        if ((ch.quarantinedUntil ?? Infinity) < (best.quarantinedUntil ?? Infinity)) {
          best = ch;
        }
      }
      return best;
    }

    // Find channels with budget for this purpose
    const eligible = healthy.filter((ch) => ch.canAccept(purpose));

    if (eligible.length > 0) {
      const ch = eligible[this.channelIndex % eligible.length];
      this.channelIndex++;
      return ch;
    }

    // No budget available — safety valve for trading (must never be blocked)
    if (purpose === 'trading') {
      // Pick the healthy channel with the lowest total rate
      let best = healthy[0];
      for (const ch of healthy) {
        if (ch.getTotalRate() < best.getTotalRate()) best = ch;
      }
      return best;
    }

    // For polling/general: round-robin through healthy channels anyway
    const ch = healthy[this.channelIndex % healthy.length];
    this.channelIndex++;
    return ch;
  }

  private async fetch<T>(method: string, path: string, body?: unknown, purpose: RequestPurpose = 'general'): Promise<T> {
    const maxAttempts = Math.min(this.channels.length + 1, 3);

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const channel = this.selectChannel(purpose);
      channel.recordRequest(purpose);
      const headers = await buildHeaders(channel.accessKey, channel.cryptoKey, method, path);

      const url = `${this.baseUrl}${path}`;
      const init: RequestInit = { method, headers: { ...headers } };
      if (body) {
        (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }

      const res = await fetch(url, init);

      if (res.status === 401 || res.status === 403) {
        console.warn(`[Kalshi] ${method} ${path} → ${res.status} with key ${channel.accessKey.slice(0, 6)}...`);
        channel.reportError(res.status);
        if (attempt < maxAttempts - 1) continue; // try next channel
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        channel.reportError(res.status);
        throw new Error(`Kalshi API ${res.status}: ${text || res.statusText}`);
      }

      // 204 No Content (e.g., cancel order)
      channel.reportSuccess();
      if (res.status === 204) return undefined as T;

      return (await res.json()) as T;
    }

    throw new Error('All keys failed authentication');
  }

  // ── Endpoints ───────────────────────────────────────────────

  async getBalance(): Promise<number> {
    const data = await this.fetch<Record<string, unknown>>('GET', '/trade-api/v2/portfolio/balance');
    const balance = pickNumber(data.balance, data.available_balance);
    return balance ?? 0;
  }

  async getPositions(): Promise<KalshiPosition[]> {
    const data = await this.fetch<Record<string, unknown>>('GET', '/trade-api/v2/portfolio/positions');

    // Unwrap — Kalshi returns varying envelope shapes
    const raw = (
      data.market_positions ?? data.positions ?? data.results ?? data.data ?? []
    ) as Record<string, unknown>[];

    if (!Array.isArray(raw)) return [];

    return raw
      .map((p): KalshiPosition | null => {
        const ticker = pickString(p.ticker, p.market_ticker);
        if (!ticker) return null;

        // Handle signed position (positive = YES, negative = NO). Kalshi
        // returns fractional positions as `position_fp` (string, e.g. "2.38")
        // with fractional contract support enabled; older integer-only
        // responses used `position`. `pickNumber` parses string numerics.
        const rawPosition = pickNumber(p.position_fp, p.position);
        let side: 'yes' | 'no';
        let position: number;

        if (rawPosition !== undefined) {
          side = rawPosition >= 0 ? 'yes' : 'no';
          position = Math.abs(rawPosition);
        } else {
          // Explicit side + position fields
          side = (p.side as 'yes' | 'no') ?? 'yes';
          position = pickNumber(p.quantity, p.total_traded) ?? 0;
        }

        if (position === 0) return null;

        const avgPrice = dollarsToCents(
          p.average_price_dollars ?? p[`${side}_price_dollars`],
          p.average_price ?? p[`${side}_price`],
        );
        const realizedPnl = dollarsToCents(p.realized_pnl_dollars, p.realized_pnl);

        return { ticker, side, position, averagePrice: avgPrice, realizedPnl };
      })
      .filter((p): p is KalshiPosition => p !== null);
  }

  async getFills(since?: string): Promise<KalshiFill[]> {
    let query = '';
    if (since) {
      // Kalshi expects min_ts as Unix seconds (int64), not ISO string
      const ts = Math.floor(new Date(since).getTime() / 1000);
      query = `?min_ts=${ts}`;
    }
    const data = await this.fetch<Record<string, unknown>>('GET', `/trade-api/v2/portfolio/fills${query}`, undefined, 'polling');

    const raw = (data.fills ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
    if (!Array.isArray(raw)) return [];

    return raw.map((f): KalshiFill => {
      const side = (f.side as 'yes' | 'no') ?? 'yes';
      const price = dollarsToCents(
        f[`${side}_price_dollars`] ?? f.price_dollars,
        f[`${side}_price`] ?? f.price,
      );

      return {
        fillId: pickString(f.fill_id, f.trade_id, f.id) ?? `fill-${Date.now()}`,
        orderId: pickString(f.order_id) ?? '',
        ticker: pickString(f.ticker, f.market_ticker) ?? '',
        side,
        action: (f.action as 'buy' | 'sell') ?? 'buy',
        count: pickNumber(f.count, f.count_fp, f.fill_count_fp, f.quantity) ?? 0,
        price,
        createdAt: pickString(f.created_at, f.created_time) ?? new Date().toISOString(),
      };
    });
  }

  async getOpenOrders(): Promise<KalshiOrder[]> {
    const data = await this.fetch<Record<string, unknown>>(
      'GET',
      '/trade-api/v2/portfolio/orders?status=resting',
      undefined,
      'polling',
    );

    const raw = (data.orders ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
    if (!Array.isArray(raw)) return [];

    return raw.map((o): KalshiOrder => {
      const side = (o.side as 'yes' | 'no') ?? 'yes';

      return {
        orderId: pickString(o.order_id, o.id) ?? '',
        ticker: pickString(o.ticker, o.market_ticker) ?? '',
        side,
        action: (o.action as 'buy' | 'sell') ?? 'buy',
        type: (o.type as 'limit' | 'market') ?? 'limit',
        status: pickString(o.status) ?? 'resting',
        count: pickNumber(o.count, o.count_fp, o.quantity) ?? 0,
        remainingCount: pickNumber(o.remaining_count, o.remaining_count_fp, o.remaining_quantity, o.quantity) ?? 0,
        yesPrice: dollarsToCents(o.yes_price_dollars, o.yes_price) || undefined,
        noPrice: dollarsToCents(o.no_price_dollars, o.no_price) || undefined,
        createdTime: pickString(o.created_time, o.created_at) ?? '',
      };
    });
  }

  async placeOrder(input: OrderInput): Promise<KalshiOrder> {
    // Build request body — send ONLY *_dollars fields (Kalshi rejects if both legacy + dollars)
    const body: Record<string, unknown> = {
      ticker: input.ticker,
      side: input.side,
      action: input.action,
      type: input.type,
      count: input.count,
      count_fp: String(input.count),
    };

    if (input.price !== undefined) {
      const dollars = (input.price / 100).toFixed(2);
      body[`${input.side}_price_dollars`] = dollars;
    }
    if (input.postOnly) body.post_only = true;
    if (input.timeInForce) body.time_in_force = input.timeInForce;
    if (input.expirationTime) body.expiration_ts = Math.floor(new Date(input.expirationTime).getTime() / 1000);

    const data = await this.fetch<Record<string, unknown>>('POST', '/trade-api/v2/portfolio/orders', body, 'trading');

    const order = (data.order ?? data) as Record<string, unknown>;
    return {
      orderId: pickString(order.order_id, order.id) ?? '',
      ticker: input.ticker,
      side: input.side,
      action: input.action,
      type: input.type,
      status: pickString(order.status) ?? 'resting',
      count: input.count,
      remainingCount: pickNumber(order.remaining_count, order.remaining_count_fp) ?? input.count,
      yesPrice: input.side === 'yes' ? input.price : undefined,
      noPrice: input.side === 'no' ? input.price : undefined,
      createdTime: pickString(order.created_time) ?? new Date().toISOString(),
    };
  }

  async cancelOrder(orderId: string): Promise<void> {
    await this.fetch<void>('DELETE', `/trade-api/v2/portfolio/orders/${orderId}`, undefined, 'trading');
  }
}
